import React, { useState, useEffect } from 'react';
import Table from '../components/common/Table';
import Pagination from '../components/common/Pagination';
import SearchBar from '../components/common/SearchBar';
import Modal from '../components/common/Modal';
import Badge from '../components/common/Badge';
import ConfirmDialog from '../components/common/ConfirmDialog';
import AdminForm from '../components/admin/AdminForm';
import { adminApi } from '../api/adminApi';
import { companyApi } from '../api/companyApi';
import { usePagination } from '../hooks/usePagination';
import { useDebounce } from '../hooks/useDebounce';
import { formatDateTime } from '../utils/formatDate';
import { ShieldCheck, Plus, Key, Power, Trash2, Building2 } from 'lucide-react';
import toast from 'react-hot-toast';

const AdminAccess = () => {
  const [admins, setAdmins] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetModalAdmin, setResetModalAdmin] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState('');
  const [adminToDelete, setAdminToDelete] = useState(null);

  const { page, setPage, limit, total, setTotal, pages } = usePagination(1, 10);
  const debouncedSearch = useDebounce(search, 400);

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAdmins({
        page,
        limit,
        search: debouncedSearch,
        status: statusFilter
      });
      setAdmins(res?.data?.admins || res?.admins || []);
      setTotal(res?.data?.pagination?.total || res?.pagination?.total || 0);
    } catch (err) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await companyApi.getCompanies({ limit: 100 });
      setCompanies(res?.data?.companies || res?.companies || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, [page, limit, debouncedSearch, statusFilter]);

  useEffect(() => {
    fetchCompanies();
  }, []);

  const handleCreateAdmin = async (formData) => {
    setSubmitting(true);
    try {
      await adminApi.createAdmin(formData);
      toast.success('Company Admin created successfully!');
      setIsAddModalOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.message || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleStatus = async (admin) => {
    const newStatus = admin.status === 'Active' ? 'Suspended' : 'Active';
    try {
      await adminApi.toggleStatus(admin._id, newStatus);
      toast.success(`Admin account ${newStatus.toLowerCase()}`);
      fetchAdmins();
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    if (!resetModalAdmin || !newPasswordInput) return;
    setSubmitting(true);
    try {
      await adminApi.resetPassword(resetModalAdmin._id, newPasswordInput);
      toast.success('Password reset successfully!');
      setResetModalAdmin(null);
      setNewPasswordInput('');
    } catch (err) {
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAdmin = async () => {
    if (!adminToDelete) return;
    setSubmitting(true);
    try {
      await adminApi.deleteAdmin(adminToDelete._id);
      toast.success('Admin account deleted');
      setAdminToDelete(null);
      fetchAdmins();
    } catch (err) {
      toast.error('Failed to delete admin');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      header: 'Admin Name',
      accessor: 'name',
      cell: (row) => (
        <div className="flex items-center space-x-3">
          <img
            src={row.avatar || 'https://images.unsplash.com/photo-1570295999919-56ceb5ecca61?w=150&auto=format&fit=crop&q=80'}
            alt={row.name}
            className="w-10 h-10 rounded-full object-cover border border-slate-100"
          />
          <div>
            <p className="font-semibold text-slate-900 font-display">{row.name}</p>
            <p className="text-xs text-slate-500">{row.email}</p>
          </div>
        </div>
      )
    },
    {
      header: 'Assigned Company',
      accessor: 'companyId',
      cell: (row) =>
        row.companyId ? (
          <div className="flex items-center space-x-2">
            <Building2 className="w-4 h-4 text-emerald-600" />
            <div>
              <p className="font-medium text-slate-900 text-xs">{row.companyId.name}</p>
              <p className="text-[10px] font-mono text-slate-500">{row.companyId.employeeIdPrefix}</p>
            </div>
          </div>
        ) : (
          <span className="text-slate-500 text-xs italic">Unassigned</span>
        )
    },
    {
      header: 'Status',
      accessor: 'status',
      cell: (row) => <Badge variant={row.status}>{row.status}</Badge>
    },
    {
      header: 'Last Login',
      accessor: 'lastLogin',
      cell: (row) => (
        <span className="text-xs text-slate-500">
          {row.lastLogin ? formatDateTime(row.lastLogin) : 'Never'}
        </span>
      )
    },
    {
      header: 'Actions',
      accessor: '_id',
      cell: (row) => (
        <div className="flex items-center space-x-1.5">
          <button
            onClick={() => {
              setResetModalAdmin(row);
              setNewPasswordInput('NewAdminPass123!');
            }}
            title="Reset Password"
            className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition"
          >
            <Key className="w-4 h-4" />
          </button>

          <button
            onClick={() => handleToggleStatus(row)}
            title={row.status === 'Active' ? 'Suspend Account' : 'Reactivate Account'}
            className={`p-1.5 rounded-lg transition ${
              row.status === 'Active'
                ? 'text-slate-500 hover:text-amber-600 hover:bg-amber-500/10'
                : 'text-amber-600 hover:text-emerald-600 hover:bg-emerald-500/10'
            }`}
          >
            <Power className="w-4 h-4" />
          </button>

          <button
            onClick={() => setAdminToDelete(row)}
            title="Delete Admin"
            className="p-1.5 rounded-lg text-slate-500 hover:text-rose-600 hover:bg-rose-500/10 transition"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )
    }
  ];

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-extrabold text-slate-900 font-display">Admin Access Control</h2>
          <p className="text-xs text-slate-500 mt-1">Manage tenant admin credentials, status, and permissions</p>
        </div>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-semibold text-xs shadow-lg shadow-emerald-600/30 flex items-center space-x-2 transition"
        >
          <Plus className="w-4 h-4" />
          <span>Create Admin Account</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 rounded-2xl bg-white border border-slate-100 shadow-sm">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by admin name or email..."
          className="w-full sm:w-80"
        />

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Suspended">Suspended</option>
        </select>
      </div>

      {/* Main Table */}
      {loading ? (
        <div className="py-16 text-center text-xs text-slate-500">Loading admins...</div>
      ) : admins.length === 0 ? (
        <div className="py-12 text-center text-xs text-slate-500">No admins found.</div>
      ) : (
        <>
          <Table columns={columns} data={admins} />
          <Pagination
            page={page}
            pages={pages}
            total={total}
            limit={limit}
            onPageChange={(p) => setPage(p)}
          />
        </>
      )}

      {/* Add Admin Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Create New Company Admin Account"
        maxWidth="max-w-xl"
      >
        <AdminForm
          companies={companies}
          onSubmit={handleCreateAdmin}
          onCancel={() => setIsAddModalOpen(false)}
          loading={submitting}
        />
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={!!resetModalAdmin}
        onClose={() => setResetModalAdmin(null)}
        title={`Reset Password for ${resetModalAdmin?.name}`}
        maxWidth="max-w-md"
      >
        <form onSubmit={handleResetPasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1.5">New Temp Password</label>
            <input
              type="text"
              required
              value={newPasswordInput}
              onChange={(e) => setNewPasswordInput(e.target.value)}
              className="w-full px-3.5 py-2 rounded-xl bg-white border border-slate-200 text-xs sm:text-sm text-slate-700 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition"
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={() => setResetModalAdmin(null)}
              className="px-4 py-2 rounded-xl text-xs font-medium bg-slate-100 text-slate-600 hover:bg-slate-200 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-xl text-xs font-semibold text-white bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 shadow-lg shadow-emerald-600/30 transition"
            >
              {submitting ? 'Resetting...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Delete */}
      <ConfirmDialog
        isOpen={!!adminToDelete}
        onClose={() => setAdminToDelete(null)}
        onConfirm={handleDeleteAdmin}
        title="Delete Admin Account?"
        message={`Are you sure you want to delete admin account ${adminToDelete?.name}?`}
        confirmText="Delete Admin"
        loading={submitting}
      />
    </div>
  );
};

export default AdminAccess;
